import React, { useEffect, useRef, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch } from 'react-redux';
import { ErrorMessage, Field, FieldArray, FieldArrayRenderProps, FieldProps, FormikErrors, FormikProvider, useFormik } from 'formik';
import { v4 as uuid } from 'uuid';
import * as Yup from 'yup';
import { AlertContent, ModalFactory, Tooltip, Wrapper } from 'components';
import { useFeatureFlags } from 'contexts/featureFlags';
import Form from 'screens/Error/Form';
import { setIsTipCalcFormDirty } from 'store/App';
import { CalculatorParticipantType, FeatureFlags, IFormCalculatorRule, IFormValues } from 'types';
import { sortAlphaNumerically } from 'utils/arrays';
import { toTitleCase } from 'utils/strings';
import { AddButton, FieldsContainer, StyledIconButton, TipCalculationsContainer } from '../../../../styles';
import { ReadOnlySelectInput } from '../../../shared/ReadOnlySelectInput';
import { ButtonContainer, ErrorText, JobsHeader, JobTitleField, PointsField, RemoveRuleTooltip, SaveButton, StyledText, StyledTitle, TipCalculationsDescription, TipCalculatorRow } from '../../../shared/styles';
import { onPointsChange } from '../../../shared/utils';

const textForShareBasedMethod = "Jobs that are included in your tip share rules will appear below. Assign the points' values for each job below.";

interface IPointValuesFormProps {
  siteId: string;
  activeRules?: IFormValues;
  isLoading?: boolean;
  onConfirm: (values: IFormValues, onSuccess?: () => void) => void;
  editPermission?: boolean;
  tabNav: React.ReactNode;
}

const PointValuesForm: React.FC<IPointValuesFormProps> = ({ siteId, activeRules, isLoading, onConfirm, editPermission, tabNav }) => {
  const dispatch = useDispatch();
  const [selectedRule, setSelectedRule] = useState<IFormCalculatorRule>();
  const deleteRuleCb = useRef<() => void>();

  const tipCalculatorRulesEnabled = useFeatureFlags()[FeatureFlags.TIP_CALCULATOR_RULES]?.enabled;

  const validationSchema = Yup.object().shape({
    rules: Yup.array().of(
      Yup.object().shape({
        contributor: Yup.object().shape({
          name: Yup.string().required('Please select a job title from the menu'),
        }),
        value: Yup.number(),
      })
    ),
  });

  const formik = useFormik({
    initialValues: { ...activeRules, rules: activeRules.rules.filter((r) => !r.default).sort(sortAlphaNumerically('contributor.name')) },
    onSubmit: (vals: IFormValues) => {
      const defaultPool = activeRules.rules.find((r) => r.default);
      const rules = defaultPool ? [...vals.rules, defaultPool] : vals.rules;
      onConfirm({ ...vals, rules });
    },
    validateOnBlur: true,
    validationSchema,
    enableReinitialize: true,
  });

  const { dirty, errors, handleSubmit, setFieldValue, touched, values } = formik;

  useEffect(() => {
    dispatch(setIsTipCalcFormDirty(dirty));
    return () => dispatch(setIsTipCalcFormDirty(false));
  }, [dirty]);

  const [showDeleteModal, hideDeleteModal] = useModal(
    () => (
      <ModalFactory customModal onClose={hideDeleteModal} isActive>
        <AlertContent
          title={`Are you sure you want to remove ${toTitleCase(selectedRule.contributor.name)}?`}
          leftActionLabel="Cancel"
          rightActionLabel="Remove"
          onClose={hideDeleteModal}
          onLeftAction={hideDeleteModal}
          onRightAction={() => {
            deleteRuleCb?.current();
            hideDeleteModal();
          }}
          rightActionAppearance="destructive"
        />
      </ModalFactory>
    ),
    [selectedRule, deleteRuleCb]
  );

  const handleRemove = (arrayHelpers: FieldArrayRenderProps, index: number) => {
    if (!values.rules[index]?.contributor?.name) {
      arrayHelpers.remove(index);
    } else {
      setSelectedRule(values.rules[index]);
      deleteRuleCb.current = () => arrayHelpers.remove(index);
      showDeleteModal();
    }
  };

  const validatePoints = (value: number) => {
    if (value < 0 || value > 100 || (!value && value !== 0)) {
      return 'Point value must be between 0 - 100';
    }
    return false;
  };

  const onAddNewJob = (arrayHelpers: FieldArrayRenderProps) => {
    const id = uuid();
    arrayHelpers.push({
      id,
      contributor: { name: undefined, type: CalculatorParticipantType.JOB },
      value: '',
    });
    requestAnimationFrame(() => {
      document.getElementById(`job-title-selector-${id}`).focus();
    });
  };

  const filterJobs = (values: IFormValues, currentRuleId: string) => {
    const jobs: string[] = [];

    // filter virtual contributors that cannot benefit from share policies
    activeRules.rules?.forEach((rule) => {
      if (rule.isVirtual && rule.contributor?.name) {
        jobs.push(rule.contributor.name);
        if (rule.additionalContributors?.length) jobs.push(...rule.additionalContributors.map((j) => j.name));
      }
    });

    // filter jobs already selected
    values.rules?.forEach((rule) => {
      if (rule.contributor?.name && rule.id !== currentRuleId) {
        jobs.push(rule.contributor.name);
      }
    });
    return jobs;
  };

  const isEditDisabled = (rule: IFormCalculatorRule) => !!rule.sharePolicy || rule.isBenefitingFromShare || rule.isBenefitingFromDefault;

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={handleSubmit} dataCy="point-values-form">
        <TipCalculationsContainer disabled={!editPermission || !tipCalculatorRulesEnabled}>
          {tabNav}
          <TipCalculationsDescription size="xsmall">{textForShareBasedMethod}</TipCalculationsDescription>
          <FieldArray
            name="rules"
            render={(arrayHelpers) => (
              <FieldsContainer>
                {values.rules && values.rules.length > 0 && (
                  <>
                    <JobsHeader>
                      <StyledTitle size="small" fontWeight="medium">
                        Job Title
                      </StyledTitle>
                      <StyledText size="small" fontWeight="medium">
                        Points
                      </StyledText>
                    </JobsHeader>
                    {values.rules.map((item, index) => (
                      <Wrapper key={item.id} direction="column" gap={0}>
                        <TipCalculatorRow>
                          <Field name={`rules.${index}.contributor`}>
                            {({ field, meta }: FieldProps) => (
                              <>
                                {!editPermission || !tipCalculatorRulesEnabled ? (
                                  <ReadOnlySelectInput text={field.value?.name} />
                                ) : (
                                  <>
                                    <JobTitleField
                                      {...field}
                                      defaultValue={field.value?.name}
                                      placeholder="Enter job title..."
                                      siteId={siteId}
                                      onChange={(job) => job && setFieldValue(`rules.${index}.contributor.name`, job.value.title)}
                                      filterOptions={filterJobs(values, item.id)}
                                      $error={!field.value?.name && meta.error && meta.touched}
                                      loadOptions={false}
                                      dataCy="job-title-selector"
                                      selectId={`job-title-selector-${item.id}`}
                                      disabled={isEditDisabled(item)}
                                      tooltipId={`share_rule_${item.id}`}
                                    />
                                    {isEditDisabled(item) && (
                                      <Tooltip id={`share_rule_${item.id}`}>
                                        {item.isBenefitingFromDefault
                                          ? 'Jobs associated with the default pool can only be changed from the Default Pool tab.'
                                          : 'Jobs associated with a tip share can only be changed from the Tip Shares tab.'}
                                      </Tooltip>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </Field>

                          <Field name={`rules.${index}.value`} validate={validatePoints}>
                            {({ field, meta }: FieldProps) => (
                              <PointsField
                                {...field}
                                inputMode="decimal"
                                onChange={(e) => onPointsChange(e, field.onChange)}
                                $error={meta.error && meta.touched}
                                disabled={!editPermission || !tipCalculatorRulesEnabled}
                                data-cy="points-input"
                              />
                            )}
                          </Field>

                          <StyledIconButton
                            label="Remove"
                            onClick={() => handleRemove(arrayHelpers, index)}
                            appearance="white"
                            size="small"
                            icon="Close"
                            data-cy="remove-rule-button"
                            disabled={!editPermission || !tipCalculatorRulesEnabled || isEditDisabled(item)}
                            tooltipId={`${item.id}_remove_disabled`}
                            $keepStyles
                            $noMargin
                          />
                          <RemoveRuleTooltip id={`${item.id}_remove_disabled`} editPermission={editPermission} featureUnavailable={!tipCalculatorRulesEnabled} disabled={isEditDisabled(item)} />
                        </TipCalculatorRow>

                        <div>
                          {!item.contributor?.name && <ErrorMessage name={`rules.${index}.contributor.name`}>{(msg) => <ErrorText size="xxsmall">{msg}</ErrorText>}</ErrorMessage>}
                          {errors?.rules && touched?.rules && (!(errors.rules[index] as FormikErrors<IFormCalculatorRule>)?.contributor || !touched.rules[index]?.contributor) && (
                            <ErrorMessage name={`rules.${index}.value`}>{(msg) => <ErrorText size="xxsmall">{msg}</ErrorText>}</ErrorMessage>
                          )}
                        </div>
                      </Wrapper>
                    ))}
                  </>
                )}
                {editPermission && tipCalculatorRulesEnabled && <AddButton appearance="white" icon="Plus" label="Add Job Title" onClick={() => onAddNewJob(arrayHelpers)} />}
              </FieldsContainer>
            )}
          />
        </TipCalculationsContainer>
        <ButtonContainer>
          <SaveButton isProcessing={isLoading} disabled={isLoading || !dirty || !tipCalculatorRulesEnabled} editPermission={editPermission} featureUnavailable={!tipCalculatorRulesEnabled} />
        </ButtonContainer>
      </Form>
    </FormikProvider>
  );
};

export default PointValuesForm;
